import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Download,
  Edit,
  OverflowMenuVertical,
  TrashCan,
  UsersAccess,
} from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogRoot,
  DialogTitle,
  Divider,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@mint-lib/tw-components';

import { ActionMenuItem } from '../../ActionMenu.jsx';
import { FolderNameInput } from './FolderNameInput.jsx';

type Props = {
  folderId: string;
  initialValue?: string;
  onCommitRename: (folderId: string, name: string) => boolean;
  onCancelRename: () => void;
  validateName: (name: string, folderId: string) => boolean;
  onDelete: () => void;
  canShare: boolean;
  onShare: () => void;
};

const FolderParentActions = ({
  onCommitRename,
  onCancelRename,
  validateName,
  folderId,
  initialValue,
  onDelete,
  canShare,
  onShare,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleShare = () => {
    setIsMenuOpen(false);
    onShare();
  };

  const handleRename = () => {
    setIsMenuOpen(false);
    setIsOpen(true);
  };

  const closeRename = () => {
    setIsOpen(false);
    onCancelRename();
  };

  const commitRename = (name: string) => {
    const renamed = onCommitRename(folderId, name);

    if (renamed) setIsOpen(false);

    return renamed;
  };

  const handleDelete = () => {
    setIsMenuOpen(false);
    onDelete();
  };

  return (
    <>
      <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            icon
            variant="outlined"
            aria-label="folder-parent-settings"
            data-dd-action-name="Knowledge base: open parent folder actions"
          >
            <OverflowMenuVertical />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-popover flex flex-col gap-1">
          {canShare && (
            <ActionMenuItem
              aria-label="share-folder"
              data-dd-action-name="Knowledge base: share parent folder"
              onClick={handleShare}
              startIcon={<UsersAccess className="fill-text-primary" />}
              label={t('Share')}
            />
          )}
          <ActionMenuItem
            aria-label="rename-folder"
            data-dd-action-name="Knowledge base: rename parent folder"
            onClick={handleRename}
            startIcon={<Edit className="fill-text-primary" />}
            label={t('Rename')}
          />
          <ActionMenuItem
            aria-label="download-folder"
            data-dd-action-name="Knowledge base: download parent folder"
            // onClick={() => {}}
            startIcon={<Download className="fill-text-primary" />}
            label={t('Download')}
          />
          <Divider />
          <ActionMenuItem
            aria-label="delete-folder"
            data-dd-action-name="Knowledge base: delete parent folder"
            onClick={handleDelete}
            startIcon={<TrashCan className="fill-error-dark" />}
            label={t('Delete')}
            tone="danger"
          />
        </PopoverContent>
      </Popover>
      <Dialog open={isOpen} modal>
        <DialogOverlay />
        <DialogRoot size={'xs'} aria-describedby={undefined}>
          <DialogHeader onClose={closeRename}>
            <DialogTitle className="flex flex-row items-center gap-2">
              {t('Rename folder')}
            </DialogTitle>
          </DialogHeader>

          <DialogContent className="flex flex-col gap-2">
            <FolderNameInput
              initialValue={initialValue}
              onCommit={commitRename}
              onCancel={closeRename}
              cancelOnBlur={false}
              validateName={(name) => validateName(name, folderId)}
            />
          </DialogContent>
        </DialogRoot>
      </Dialog>
    </>
  );
};

export { FolderParentActions };
